/**
 * 페이지별 안내 메시지 다국어 지원 (Settings, Contact, Landing, Works 등)
 *
 * 사용법:
 * import { getSettingsMessage, getWorksMessage } from '@src/shared/constants/pageMessages';
 * import { getContactsMessage, getLandingMessage, getPageMessage } from '@src/shared/constants/pageMessages';
 */

import type { LocalizedMessages } from '../dto/EntityRepository';
import { AboutContentMode, CareerType, ExposureLevel, NavLayout } from '../enum/EnumRepository';
import { useAboutViewStore } from '../store/aboutViewStore';
import { useLanguageStore, Language } from '../store/languageStore';

export type landingMessagesKey =
  | 'LANDING_SLOGAN_SUB'
  | 'LANDING_CREATE_OWN'
  | 'LANDING_BROWSE_OTHERS'
  | 'LANDING_SEARCH_PLACEHOLDER'
  | 'LANDING_SEARCH_TITLE'
  | 'LANDING_SEARCH_EMPTY'
  | 'LANDING_SEARCH_SEARCHING'
  | 'LANDING_SEARCH_NO_RESULTS'
  | 'LANDING_SEARCH_FAILED'
  | 'LANDING_ARCHIVE_SEARCH_RESULTS';

  const landingMessages: LocalizedMessages<landingMessagesKey> = {
    LANDING_SLOGAN_SUB: {
      [Language.EN]: "Collect your works and share them as a portfolio.",
      [Language.KR]: "작품을 정리하고 포트폴리오로 활용하세요.",
  },
    LANDING_CREATE_OWN: {
      [Language.EN]: "Would you like to create your own?",
      [Language.KR]: "나의 Archive 만들기",
  },
    LANDING_BROWSE_OTHERS: {
      [Language.EN]: "Would you like to browse other artists?",
      [Language.KR]: "다른 작가의 Archive 검색하기",
  },
  LANDING_SEARCH_PLACEHOLDER: {
    [Language.EN]: "Find Artist",
    [Language.KR]: "작가 검색",
  },
  LANDING_SEARCH_TITLE: {
    [Language.EN]: "Find Artist",
    [Language.KR]: "작가 검색",
  },
  LANDING_SEARCH_EMPTY: {
    [Language.EN]: "Search by artist name.",
    [Language.KR]: "작가 이름으로 검색하세요.",
  },
  LANDING_SEARCH_SEARCHING: {
    [Language.EN]: "Searching…",
    [Language.KR]: "검색 중…",
  },
  LANDING_SEARCH_NO_RESULTS: {
    [Language.EN]: "No results.",
    [Language.KR]: "검색 결과가 없습니다.",
  },
  LANDING_SEARCH_FAILED: {
    [Language.EN]: "Search failed.",
    [Language.KR]: "검색에 실패했습니다.",
  },
  LANDING_ARCHIVE_SEARCH_RESULTS: {
    [Language.EN]: "Search results",
    [Language.KR]: "검색 결과",
  },
};

export type contactsMessagesKey =
  | 'CONTACT_MENU_VISIBILITY_HINT'
  | 'CONTACT_PLACEHOLDER_ADDRESS'
  | 'CONTACT_DEFAULT_ADDRESS'
  | 'CONTACT_PLACEHOLDER_EMAIL'
  | 'CONTACT_DEFAULT_EMAIL'
  | 'CONTACT_PLACEHOLDER_CONTACT'
  | 'CONTACT_DEFAULT_CONTACT'
  | 'CONTACT_DEFAULT_INSTAGRAM'
  | 'CONTACT_PLACEHOLDER_INSTAGRAM'
  | 'CONTACT_DEFAULT_TWITTER'
  | 'CONTACT_PLACEHOLDER_TWITTER'
  | 'CONTACT_DEFAULT_FACEBOOK'
  | 'CONTACT_PLACEHOLDER_FACEBOOK'
  | 'CONTACT_DEFAULT_URL1'
  | 'CONTACT_PLACEHOLDER_URL1';

const contactsMessages: LocalizedMessages<contactsMessagesKey> = {
  CONTACT_MENU_VISIBILITY_HINT: {
    [Language.EN]: "If you prefer not to use this page, go to Settings > Menu Visibility.",
    [Language.KR]: "Contact를 사용하지 않으려면 Settings > Menu Visibility에서 비공개로 설정하세요.",
  },
  CONTACT_PLACEHOLDER_ADDRESS: {
    [Language.EN]: "Enter your address",
    [Language.KR]: "작업실 주소",
  },
  CONTACT_DEFAULT_ADDRESS: {
    [Language.EN]: "e.g. 000-000, Street Name, City, Region, Country",
    [Language.KR]: "예) 도로명 주소, 시/군/구, 시/도, 국가",
  },
  CONTACT_PLACEHOLDER_EMAIL: {
    [Language.EN]: "email",
    [Language.KR]: "이메일",
  },
  CONTACT_DEFAULT_EMAIL: {
    [Language.EN]: "e.g. username@example.com",
    [Language.KR]: "예) username@example.com",
  },
  CONTACT_PLACEHOLDER_CONTACT: {
    [Language.EN]: "contact",
    [Language.KR]: "연락처",
  },
  CONTACT_DEFAULT_CONTACT: {
    [Language.EN]: "e.g. +12-3456-7890",
    [Language.KR]: "예) +82-10-1234-5678",
  },
  CONTACT_DEFAULT_INSTAGRAM: {
    [Language.EN]: "Instagram",
    [Language.KR]: "Instagram",
  },
  CONTACT_PLACEHOLDER_INSTAGRAM: {
    [Language.EN]: "Instagram ID",
    [Language.KR]: "Instagram 아이디",
  },
  CONTACT_DEFAULT_TWITTER: {
    [Language.EN]: "X(twitter)",
    [Language.KR]: "X(트위터)",
  },
  CONTACT_PLACEHOLDER_TWITTER: {
    [Language.EN]: "X(twitter) ID",
    [Language.KR]: "X(트위터) 아이디",
  },
  CONTACT_DEFAULT_FACEBOOK: {
    [Language.EN]: "facebook",
    [Language.KR]: "페이스북",
  },
  CONTACT_PLACEHOLDER_FACEBOOK: {
    [Language.EN]: "Facebook ID",
    [Language.KR]: "페이스북 아이디",
  },
  CONTACT_DEFAULT_URL1: {
    [Language.EN]: "your own website",
    [Language.KR]: "웹사이트 주소",
  },
  CONTACT_PLACEHOLDER_URL1: {
    [Language.EN]: "URL",
    [Language.KR]: "URL",
  },
};
  
export type PageMessageKey =
  | 'NO_WORKS_FOUND'
  | 'NO_TEXTS_FOUND'
  | 'NO_DATA_FOUND'
  | 'ADD_FIRST'
  | 'NO_PROJECTS_FOUND'
  | 'NO_CAREER_FOUND'
  | 'CAREER_RELATED_URL_PLACEHOLDER'
  | 'SELECT_TEXT_TO_VIEW'
  | 'DRAG_DROP_CREATE_WORK'
  | 'DRAG_DROP_CREATE_DETAIL'
  | 'DRAG_DROP_CREATE_TEXT_IMAGE'
  | 'DRAG_DROP_LIMIT_TEXT'
  | 'DRAG_DROP_TEXT_IMAGE_LIMIT'
  | 'IMPORT_STATION_EMPTY_WORK'
  | 'IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS'
  | 'IMPORT_STATION_EMPTY_TEXT'
  | 'TEXT_COPY_IMPORT_TITLE'
  | 'TEXT_COPY_IMPORT_EXPLAIN'
  | 'TEXT_COPY_IMPORT_ACCOUNT_LABEL'
  | 'TEXT_COPY_IMPORT_SOURCE_LOADING'
  | 'TEXT_COPY_IMPORT_SOURCE_EMPTY'
  | 'TEXT_COPY_IMPORT_SOURCE_FAILED'
  | 'TEXT_COPY_IMPORT_GROUP_MEMBER_INACTIVE'
  | 'TEXT_COPY_IMPORT_ACCOUNT_INACTIVE'
  | 'TEXT_COPY_IMPORT_ACCOUNT_PRIVATE'
  | 'TEXT_COPY_IMPORT_TEXTS_PRIVATE'
  | 'TEXT_COPY_IMPORT_SOURCE_UNAVAILABLE'
  | 'TEXT_COPY_IMPORT_TEXT_LOADING'
  | 'TEXT_COPY_IMPORT_TEXT_EMPTY'
  | 'TEXT_COPY_IMPORT_TEXT_FAILED'
  | 'TEXT_COPY_IMPORT_PENDING'
  | 'TEXT_COPY_IMPORT_SELECTED'
  | 'TEXT_COPY_IMPORT_REQUEST'
  | 'TEXT_COPY_IMPORT_REQUESTING'
  | 'TEXT_COPY_IMPORT_SENT'
  | 'TEXT_COPY_IMPORT_UNTITLED'
  | 'WORK_COPY_IMPORT_TITLE'
  | 'WORK_COPY_IMPORT_EXPLAIN'
  | 'WORK_COPY_IMPORT_ACCOUNT_LABEL'
  | 'WORK_COPY_IMPORT_SOURCE_LOADING'
  | 'WORK_COPY_IMPORT_SOURCE_EMPTY'
  | 'WORK_COPY_IMPORT_SOURCE_FAILED'
  | 'WORK_COPY_IMPORT_GROUP_MEMBER_INACTIVE'
  | 'WORK_COPY_IMPORT_ACCOUNT_INACTIVE'
  | 'WORK_COPY_IMPORT_ACCOUNT_PRIVATE'
  | 'WORK_COPY_IMPORT_WORKS_PRIVATE'
  | 'WORK_COPY_IMPORT_ACCOUNT_NOT_ARTIST'
  | 'WORK_COPY_IMPORT_SOURCE_UNAVAILABLE'
  | 'WORK_COPY_IMPORT_WORK_LOADING'
  | 'WORK_COPY_IMPORT_WORK_EMPTY'
  | 'WORK_COPY_IMPORT_LABEL_EMPTY'
  | 'WORK_COPY_IMPORT_WORK_FAILED'
  | 'WORK_COPY_IMPORT_PENDING'
  | 'WORK_COPY_IMPORT_SELECTED'
  | 'WORK_COPY_IMPORT_REQUEST'
  | 'WORK_COPY_IMPORT_REQUESTING'
  | 'WORK_COPY_IMPORT_SENT'
  | 'WORK_COPY_IMPORT_UNTITLED'
  | 'WORK_COPY_IMPORT_SELECT_EMPTY'
  | 'MEMBER_NAME_DEFAULT'
  | 'MEMBER_NAME_PLACEHOLDER'
  | 'GROUP_MEMBER_INVITATION_PENDING'
  | 'GROUP_MEMBER_CONNECTED_ACCOUNT'
  | 'GROUP_MEMBER_CONNECTION_HINT'
  | 'PROJECT_TITLE_PLACEHOLDER'
  | 'PROJECT_INFO_NAME_PLACEHOLDER'
  | 'DESCRIPTION_PLACEHOLDER_DEFAULT'
  | 'PROJECT_INFO_VALUE_PLACEHOLDER'
  | 'DETAIL_DESCRIPTION_PLACEHOLDER'
  | 'DOWNLOAD_TOGGLE_LABEL'
  | 'NO_ITEMS_TO_REORDER'
  | 'WORK_INDEXING_HINT_SELECT_SIDEBAR'
  | 'WORK_INDEXING_HINT_SELECT_RIGHT'
  | 'WORK_INDEXING_HINT_DRAG_REORDER'
  | 'WORK_INDEXING_HINT_UNLISTED_KEEP_ORDER'
  | 'WORK_INDEXING_EMPTY_SELECT_WORK_FOR_ORDERING'
  | 'TEXT_INDEXING_HINT_SELECT_SIDEBAR'
  | 'TEXT_INDEXING_HINT_DRAG_REORDER'
  | 'TEXT_INDEXING_HINT_UNLISTED_KEEP_ORDER'
  | 'TEXT_INDEXING_EMPTY_SELECT_TEXT_FOR_ORDERING'
  | 'EXHIBITION_NO_MATCHING_RESULTS'
  | 'EXHIBITION_VIEW_SELECTOR_LABEL'
  | 'EXHIBITION_VIEW_ALL_VENUES'
  | 'EXHIBITION_VIEW_CURRENT_EXHIBITIONS'
  | 'EXHIBITION_VIEW_ALL_VENUES_MOBILE'
  | 'EXHIBITION_VIEW_CURRENT_EXHIBITIONS_MOBILE'
  | 'EXHIBITION_NO_REGISTERED_EXHIBITIONS';

const pageMessages: LocalizedMessages<PageMessageKey> = {
  NO_WORKS_FOUND: {
    [Language.EN]: "No works found.",
    [Language.KR]: "등록된 작품이 없습니다.",
  },
  NO_TEXTS_FOUND: {
    [Language.EN]: "No texts found.",
    [Language.KR]: "등록된 Text가 없습니다.",
  },
  NO_DATA_FOUND: {
    [Language.EN]: "No data found.",
    [Language.KR]: "데이터가 없습니다.",
  },
  EXHIBITION_NO_MATCHING_RESULTS: {
    [Language.EN]: "No results match the selected filters.",
    [Language.KR]: "조건에 맞는 결과가 없습니다.",
  },
  EXHIBITION_VIEW_SELECTOR_LABEL: {
    [Language.EN]: "Exhibition index view",
    [Language.KR]: "전시 목록 보기 방식",
  },
  EXHIBITION_VIEW_ALL_VENUES: {
    [Language.EN]: "Venues",
    [Language.KR]: "전시 공간",
  },
  EXHIBITION_VIEW_CURRENT_EXHIBITIONS: {
    [Language.EN]: "On view",
    [Language.KR]: "현재 전시",
  },
  EXHIBITION_VIEW_ALL_VENUES_MOBILE: {
    [Language.EN]: "Venue",
    [Language.KR]: "공간",
  },
  EXHIBITION_VIEW_CURRENT_EXHIBITIONS_MOBILE: {
    [Language.EN]: "Show",
    [Language.KR]: "전시",
  },
  EXHIBITION_NO_REGISTERED_EXHIBITIONS: {
    [Language.EN]: "No exhibitions are registered for this venue.",
    [Language.KR]: "이 공간에 등록된 전시가 없습니다.",
  },
  ADD_FIRST: {
    [Language.EN]: "Add a {type} first.",
    [Language.KR]: "먼저 {type}을 추가해주세요.",
  },
  NO_PROJECTS_FOUND: {
    [Language.EN]: "No projects found.",
    [Language.KR]: "등록된 Projects가 없습니다.",
  },
  NO_CAREER_FOUND: {
    [Language.EN]: "No career found.",
    [Language.KR]: "등록된 Career가 없습니다.",
  },
  CAREER_RELATED_URL_PLACEHOLDER: {
    [Language.EN]: "Related URL",
    [Language.KR]: "관련 URL",
  },
  SELECT_TEXT_TO_VIEW: {
    [Language.EN]: "Select a text to view.",
    [Language.KR]: "Text를 선택해주세요.",
  },
  DRAG_DROP_CREATE_WORK: {
    [Language.EN]: "Drop image to create work",
    [Language.KR]: "이미지를 드래그하여 놓으면 작품이 생성됩니다",
  },
  DRAG_DROP_CREATE_DETAIL: {
    [Language.EN]: "Drop image to create detail",
    [Language.KR]: "이미지를 드래그하여 놓으면 Detail Cut이 생성됩니다",
  },
  DRAG_DROP_CREATE_TEXT_IMAGE: {
    [Language.EN]: "Drop image to create image block",
    [Language.KR]: "이미지를 드래그하여 놓으면 Image Block이 생성됩니다",
  },
  DRAG_DROP_LIMIT_TEXT: {
    [Language.EN]: "Max {maxCount} images • JPEG, JPG, PNG only",
    [Language.KR]: "최대 {maxCount}개 • JPEG, JPG, PNG 형식만 가능합니다",
  },
  DRAG_DROP_TEXT_IMAGE_LIMIT: {
    [Language.EN]: "{remainingCount} remaining • Max {maxCount} per text",
    [Language.KR]: "{remainingCount}개 추가 가능 • Text당 최대 {maxCount}개",
  },
  IMPORT_STATION_EMPTY_WORK: {
    [Language.EN]: "Work does not exist.\nCreate Work first.",
    [Language.KR]: "등록된 작품이 없습니다.\nWorks에서 등록 후 다시 시도해주세요.",
  },
  IMPORT_STATION_NO_WORK_FOR_SELECTED_TAGS: {
    [Language.EN]: "No works match the selected tags.",
    [Language.KR]: "선택한 태그에 맞는 작품이 없습니다.",
  },
  IMPORT_STATION_EMPTY_TEXT: {
    [Language.EN]: "Text does not exist.\nCreate Text first.",
    [Language.KR]: "등록된 Text가 없습니다.\nTexts에서 등록 후 다시 시도해주세요.",
  },
  TEXT_COPY_IMPORT_TITLE: {
    [Language.EN]: "Import Texts",
    [Language.KR]: "Text 가져오기",
  },
  TEXT_COPY_IMPORT_EXPLAIN: {
    [Language.EN]: "Select public Texts from a connected account.\nCopies are created only after that account approves the request.",
    [Language.KR]: "연결된 계정의 공개 Text를 선택하세요.\n상대 계정이 요청을 승인하면 독립된 복사본이 생성됩니다.",
  },
  TEXT_COPY_IMPORT_ACCOUNT_LABEL: {
    [Language.EN]: "Connected account",
    [Language.KR]: "연결된 계정",
  },
  TEXT_COPY_IMPORT_SOURCE_LOADING: {
    [Language.EN]: "Loading connected accounts...",
    [Language.KR]: "연결된 계정을 불러오는 중입니다...",
  },
  TEXT_COPY_IMPORT_SOURCE_EMPTY: {
    [Language.EN]: "There are no connected accounts.",
    [Language.KR]: "연결된 계정이 없습니다.",
  },
  TEXT_COPY_IMPORT_SOURCE_FAILED: {
    [Language.EN]: "Connected accounts could not be loaded.",
    [Language.KR]: "연결된 계정을 불러오지 못했습니다.",
  },
  TEXT_COPY_IMPORT_GROUP_MEMBER_INACTIVE: {
    [Language.EN]: "This Group Member is inactive, so Texts cannot be requested.",
    [Language.KR]: "선택한 Group Member가 비활성 상태라 Text를 요청할 수 없습니다.",
  },
  TEXT_COPY_IMPORT_ACCOUNT_INACTIVE: {
    [Language.EN]: "This connected account is currently inactive.",
    [Language.KR]: "선택한 연결 계정이 현재 비활성 상태입니다.",
  },
  TEXT_COPY_IMPORT_ACCOUNT_PRIVATE: {
    [Language.EN]: "This connected account is private.",
    [Language.KR]: "선택한 연결 계정은 private 계정입니다.",
  },
  TEXT_COPY_IMPORT_TEXTS_PRIVATE: {
    [Language.EN]: "This account's Texts menu is private.\nPlease contact the artist to request access.",
    [Language.KR]: "선택한 계정의 Texts 메뉴가 private입니다.\n가져오기가 필요하다면 작가에게 공개를 요청해주세요.",
  },
  TEXT_COPY_IMPORT_SOURCE_UNAVAILABLE: {
    [Language.EN]: "This connected account is not currently available for Text import.",
    [Language.KR]: "선택한 연결 계정에서는 현재 Text를 가져올 수 없습니다.",
  },
  TEXT_COPY_IMPORT_TEXT_LOADING: {
    [Language.EN]: "Loading Texts...",
    [Language.KR]: "Text를 불러오는 중입니다...",
  },
  TEXT_COPY_IMPORT_TEXT_EMPTY: {
    [Language.EN]: "No public Texts are currently available.",
    [Language.KR]: "현재 가져올 수 있는 공개 Text가 없습니다.",
  },
  TEXT_COPY_IMPORT_TEXT_FAILED: {
    [Language.EN]: "Texts could not be loaded.",
    [Language.KR]: "Text를 불러오지 못했습니다.",
  },
  TEXT_COPY_IMPORT_PENDING: {
    [Language.EN]: "Approval pending",
    [Language.KR]: "승인 대기 중",
  },
  TEXT_COPY_IMPORT_SELECTED: {
    [Language.EN]: "Selected {count} / 10",
    [Language.KR]: "{count} / 10개 선택",
  },
  TEXT_COPY_IMPORT_REQUEST: {
    [Language.EN]: "Request usage approval",
    [Language.KR]: "사용 승인 요청",
  },
  TEXT_COPY_IMPORT_REQUESTING: {
    [Language.EN]: "Requesting...",
    [Language.KR]: "요청 중...",
  },
  TEXT_COPY_IMPORT_SENT: {
    [Language.EN]: "The usage approval request was sent. You can check its status in Settings > Requests.",
    [Language.KR]: "사용 승인 요청을 보냈습니다. Settings > 요청에서 상태를 확인할 수 있습니다.",
  },
  TEXT_COPY_IMPORT_UNTITLED: {
    [Language.EN]: "Untitled Text",
    [Language.KR]: "제목 없는 Text",
  },
  WORK_COPY_IMPORT_TITLE: {
    [Language.EN]: "Import Works",
    [Language.KR]: "Work 가져오기",
  },
  WORK_COPY_IMPORT_EXPLAIN: {
    [Language.EN]: "Select public Works from a connected account.\nCopies are created only after that account approves the request.",
    [Language.KR]: "연결된 계정의 공개 Work를 선택하세요.\n상대 계정이 요청을 승인하면 독립된 복사본이 생성됩니다.",
  },
  WORK_COPY_IMPORT_ACCOUNT_LABEL: {
    [Language.EN]: "Connected account",
    [Language.KR]: "연결된 계정",
  },
  WORK_COPY_IMPORT_SOURCE_LOADING: {
    [Language.EN]: "Loading connected accounts...",
    [Language.KR]: "연결된 계정을 불러오는 중입니다...",
  },
  WORK_COPY_IMPORT_SOURCE_EMPTY: {
    [Language.EN]: "There are no connected accounts.",
    [Language.KR]: "연결된 계정이 없습니다.",
  },
  WORK_COPY_IMPORT_SOURCE_FAILED: {
    [Language.EN]: "Connected accounts could not be loaded.",
    [Language.KR]: "연결된 계정을 불러오지 못했습니다.",
  },
  WORK_COPY_IMPORT_GROUP_MEMBER_INACTIVE: {
    [Language.EN]: "This Group Member is inactive, so Works cannot be requested.",
    [Language.KR]: "선택한 Group Member가 비활성 상태라 Work를 요청할 수 없습니다.",
  },
  WORK_COPY_IMPORT_ACCOUNT_INACTIVE: {
    [Language.EN]: "This connected account is currently inactive.",
    [Language.KR]: "선택한 연결 계정이 현재 비활성 상태입니다.",
  },
  WORK_COPY_IMPORT_ACCOUNT_PRIVATE: {
    [Language.EN]: "This connected account is private.",
    [Language.KR]: "선택한 연결 계정은 private 계정입니다.",
  },
  WORK_COPY_IMPORT_WORKS_PRIVATE: {
    [Language.EN]: "This account's Works menu is private.\nPlease contact the artist to request access.",
    [Language.KR]: "선택한 계정의 Works 메뉴가 private입니다.\n가져오기가 필요하다면 작가에게 공개를 요청해주세요.",
  },
  WORK_COPY_IMPORT_ACCOUNT_NOT_ARTIST: {
    [Language.EN]: "Only Works from an artist account can be requested.",
    [Language.KR]: "작가 계정의 Work만 사용 요청할 수 있습니다.",
  },
  WORK_COPY_IMPORT_SOURCE_UNAVAILABLE: {
    [Language.EN]: "This connected account is not currently available for Work import.",
    [Language.KR]: "선택한 연결 계정에서는 현재 Work를 가져올 수 없습니다.",
  },
  WORK_COPY_IMPORT_WORK_LOADING: {
    [Language.EN]: "Loading Works...",
    [Language.KR]: "Work를 불러오는 중입니다...",
  },
  WORK_COPY_IMPORT_WORK_EMPTY: {
    [Language.EN]: "No public Works are currently available.",
    [Language.KR]: "현재 가져올 수 있는 공개 Work가 없습니다.",
  },
  WORK_COPY_IMPORT_LABEL_EMPTY: {
    [Language.EN]: "No public Works match the selected Labels.",
    [Language.KR]: "선택한 Label에 해당하는 공개 Work가 없습니다.",
  },
  WORK_COPY_IMPORT_WORK_FAILED: {
    [Language.EN]: "Works could not be loaded.",
    [Language.KR]: "Work를 불러오지 못했습니다.",
  },
  WORK_COPY_IMPORT_PENDING: {
    [Language.EN]: "Approval pending",
    [Language.KR]: "승인 대기 중",
  },
  WORK_COPY_IMPORT_SELECTED: {
    [Language.EN]: "Selected {count} / 10",
    [Language.KR]: "{count} / 10개 선택",
  },
  WORK_COPY_IMPORT_REQUEST: {
    [Language.EN]: "Request usage approval",
    [Language.KR]: "사용 승인 요청",
  },
  WORK_COPY_IMPORT_REQUESTING: {
    [Language.EN]: "Requesting...",
    [Language.KR]: "요청 중...",
  },
  WORK_COPY_IMPORT_SENT: {
    [Language.EN]: "The usage approval request was sent. You can check its status in Settings > Requests.",
    [Language.KR]: "사용 승인 요청을 보냈습니다. Settings > 요청에서 상태를 확인할 수 있습니다.",
  },
  WORK_COPY_IMPORT_UNTITLED: {
    [Language.EN]: "Untitled Work",
    [Language.KR]: "제목 없는 Work",
  },
  WORK_COPY_IMPORT_SELECT_EMPTY: {
    [Language.EN]: "Select a Work.",
    [Language.KR]: "Work를 선택해주세요.",
  },
  MEMBER_NAME_DEFAULT: {
    [Language.EN]: "Basic Info & Career",
    [Language.KR]: "기본 정보 및 경력",
  },
  MEMBER_NAME_PLACEHOLDER: {
    [Language.EN]: "Artist Name",
    [Language.KR]: "작가명",
  },
  GROUP_MEMBER_INVITATION_PENDING: {
    [Language.EN]: "Invitation pending",
    [Language.KR]: "응답 대기 중",
  },
  GROUP_MEMBER_CONNECTED_ACCOUNT: {
    [Language.EN]: "Connected account",
    [Language.KR]: "연결된 계정",
  },
  GROUP_MEMBER_CONNECTION_HINT: {
    [Language.EN]: "This connection confirms account identity/relationship only. The Group Member content is written and managed by the group.",
    [Language.KR]: "이 연결은 계정의 동일성·관계만 확인합니다. GroupMember 내용은 그룹이 작성하고 관리합니다.",
  },
  PROJECT_TITLE_PLACEHOLDER: {
    [Language.EN]: "Title",
    [Language.KR]: "프로젝트 제목",
  },
  PROJECT_INFO_NAME_PLACEHOLDER: {
    [Language.EN]: "Credit Info",
    [Language.KR]: "크레딧 정보",
  },

  DESCRIPTION_PLACEHOLDER_DEFAULT: {
    [Language.EN]: "Enter a brief description",
    [Language.KR]: "간단한 설명을 입력하세요",
  },
  PROJECT_INFO_VALUE_PLACEHOLDER: {
    [Language.EN]: "location, collaborator, sponsor, etc.",
    [Language.KR]: "기간, 장소/위치, 협업자, 후원사 등",
  },
  DETAIL_DESCRIPTION_PLACEHOLDER: {
    [Language.EN]: "Add an image credit or a brief note",
    [Language.KR]: "이미지 출처 또는 간단한 설명을 입력하세요",
  },
  DOWNLOAD_TOGGLE_LABEL: {
    [Language.EN]: "Download",
    [Language.KR]: "다운로드",
  },
  NO_ITEMS_TO_REORDER: {
    [Language.EN]: "No items to reorder",
    [Language.KR]: "순서를 변경할 항목이 없습니다",
  },
  WORK_INDEXING_HINT_SELECT_SIDEBAR: {
    [Language.EN]: "On the left, choose the works you want to order.",
    [Language.KR]: "왼쪽에서 순서를 부여할 작품을 선택하세요.",
  },
  WORK_INDEXING_HINT_SELECT_RIGHT: {
    [Language.EN]: "↓ Choose the works you want to order.",
    [Language.KR]: "↓ 순서를 부여할 작품을 선택하세요.",
  },
  WORK_INDEXING_HINT_DRAG_REORDER: {
    [Language.EN]:
      "Drag to reorder.\nSelect several rows first to move them together.",
    [Language.KR]:
      "드래그로 순서를 바꿀 수 있습니다.\n여러 항목을 선택한 후 함께 이동할 수 있습니다.",
  },
  WORK_INDEXING_HINT_UNLISTED_KEEP_ORDER: {
    [Language.EN]: "Works not included here stay in their current order.",
    [Language.KR]: "순서가 부여되지 않은 작품은 기존 순서를 유지합니다.",
  },
  WORK_INDEXING_EMPTY_SELECT_WORK_FOR_ORDERING: {
    [Language.EN]: "No works have been assigned an order.",
    [Language.KR]: "순서가 부여된 작품이 없습니다.",
  },
  TEXT_INDEXING_HINT_SELECT_SIDEBAR: {
    [Language.EN]: "On the left, choose the texts you want to order.",
    [Language.KR]: "왼쪽에서 순서를 부여할 Text를 선택하세요.",
  },
  TEXT_INDEXING_HINT_DRAG_REORDER: {
    [Language.EN]:
      "Drag to reorder. Select several rows first to move them together.",
    [Language.KR]:
      "드래그로 순서를 바꿀 수 있습니다. 여러 항목은 선택하신 뒤 함께 옮겨 주세요.",
  },
  TEXT_INDEXING_HINT_UNLISTED_KEEP_ORDER: {
    [Language.EN]: "Texts not included here stay in their current order.",
    [Language.KR]: "순서가 부여되지 않은 Text는 기존 순서를 유지합니다.",
  },
  TEXT_INDEXING_EMPTY_SELECT_TEXT_FOR_ORDERING: {
    [Language.EN]: "No texts have been assigned an order.",
    [Language.KR]: "순서가 부여된 Text가 없습니다.",
  },
};

export type worksMessagesKey =
  | 'RELATED_PROJECT';

const worksMessages: LocalizedMessages<worksMessagesKey> = {
  RELATED_PROJECT: {
    [Language.EN]: "Related Projects",
    [Language.KR]: "연관된 프로젝트",
  },
};

export type SettingsMessageKey =
  // Settings section titles
  | 'SETTINGS_EMAIL'
  | 'SETTINGS_ACTIVITY_TYPE'
  | 'SETTINGS_PASSWORD'
  | 'SETTINGS_MARKETING'
  | 'SETTINGS_APPROVAL_REQUESTS'
  | 'SETTINGS_CONNECTIONS'
  | 'SETTINGS_PAGE_VISIBILITY'
  | 'SETTINGS_MENU_VISIBILITY'
  | 'SETTINGS_NAV_LAYOUT'
  | 'SETTINGS_SHARE'
  | 'SETTINGS_SEARCH'
  | 'SETTINGS_STORAGE'
  | 'SEARCH_PREVIEW_OG_IMAGE_CUSTOM'
  | 'SEARCH_PREVIEW_OG_IMAGE_DEFAULT'
  // MemberSetting SubExplain
  | 'LINK_ID_EXPLAIN'
  | 'USERNAME_EXPLAIN'
  | 'SHARE_EXPLAIN'
  | 'SEARCH_EXPLAIN'
  // PageSetting — ExposureLevel 별 SubExplain
  | 'PAGE_EXPOSURE_PRIVATE_EXPLAIN'
  | 'PAGE_EXPOSURE_SEARCHABLE_EXPLAIN'
  | 'PAGE_EXPOSURE_PUBLIC_EXPLAIN'
  | 'NAV_LAYOUT_EXPLAIN'
  | 'NAV_LAYOUT_FOLD'
  | 'NAV_LAYOUT_FIXED'
  // Subscription SubExplain
  | 'SUBSCRIPTION_MANAGED_BY'
  | 'PAYMENT_PROCESSOR'
  | 'POLAR_OPEN_SUBSCRIPTION_PAGE'
  | 'POLAR_OPEN_PAYMENT_PAGE'
  // Subscription — ProductCard (SubscriptionModal)
  | 'SUBSCRIPTION_PRODUCT_INTRO'
  | 'SUBSCRIPTION_PRICE_INTERVAL_MONTH'
  | 'SUBSCRIPTION_PRICE_INTERVAL_YEAR'
  | 'SUBSCRIPTION_PRODUCT_FEATURE_STORAGE'
  | 'SUBSCRIPTION_PRODUCT_FEATURE_UPLOADS'
  | 'SUBSCRIPTION_PRODUCT_FEATURE_VISIBILITY'
  | 'SUBSCRIPTION_PRODUCT_FEATURE_CANCEL'
  // Notification SubExplain
  | 'MARKETING_EMAIL_EXPLAIN'
  | 'APPROVAL_REQUESTS_EXPLAIN'
  | 'CONNECTIONS_EXPLAIN'
  | 'CONNECTION_NONE'
  | 'CONNECTION_COUNT'
  | 'CONNECTION_LOADING'
  | 'CONNECTION_FAILED'
  | 'APPROVAL_REQUEST_EMPTY'
  | 'APPROVAL_REQUEST_NONE'
  | 'APPROVAL_REQUEST_COUNT'
  | 'APPROVAL_REQUEST_LOADING'
  | 'APPROVAL_REQUEST_FAILED'
  | 'APPROVAL_REQUEST_ACTION_FAILED'
  | 'APPROVAL_REQUEST_PROCESSING'
  | 'GROUP_MEMBER_CONNECTION_REQUEST_SUBJECT'
  | 'GROUP_MEMBER_CONNECTION_REQUEST_EXPLAIN'
  | 'GROUP_MEMBER_CONNECTION_INCOMING_PENDING'
  | 'GROUP_MEMBER_CONNECTION_INCOMING_ACCEPTED'
  | 'GROUP_MEMBER_CONNECTION_INCOMING_REJECTED'
  | 'GROUP_MEMBER_CONNECTION_INCOMING_CANCELED'
  | 'GROUP_MEMBER_CONNECTION_INCOMING_EXPIRED'
  | 'WORK_USAGE_REQUEST_SUBJECT'
  | 'TEXT_COPY_REQUEST_SUBJECT'
  | 'TEXT_COPY_REQUEST_UNAVAILABLE'
  | 'TEXT_COPY_ITEM_UNAVAILABLE'
  | 'WORK_COPY_REQUEST_SUBJECT'
  | 'WORK_COPY_REQUEST_UNAVAILABLE'
  | 'WORK_COPY_ITEM_UNAVAILABLE'
  | 'APPROVAL_REQUEST_INCOMING_PENDING'
  | 'APPROVAL_REQUEST_OUTGOING_PENDING'
  | 'APPROVAL_REQUEST_INCOMING_ACCEPTED'
  | 'APPROVAL_REQUEST_OUTGOING_ACCEPTED'
  | 'APPROVAL_REQUEST_INCOMING_REJECTED'
  | 'APPROVAL_REQUEST_OUTGOING_REJECTED'
  | 'APPROVAL_REQUEST_INCOMING_CANCELED'
  | 'APPROVAL_REQUEST_OUTGOING_CANCELED'
  | 'APPROVAL_REQUEST_INCOMING_EXPIRED'
  | 'APPROVAL_REQUEST_OUTGOING_EXPIRED';

const settingsMessages: LocalizedMessages<SettingsMessageKey> = {
  SETTINGS_EMAIL: {
    [Language.EN]: "Email",
    [Language.KR]: "이메일",
  },
  SETTINGS_ACTIVITY_TYPE: {
    [Language.EN]: "Activity type",
    [Language.KR]: "활동 유형",
  },
  SETTINGS_PASSWORD: {
    [Language.EN]: "Password",
    [Language.KR]: "비밀번호",
  },
  SETTINGS_MARKETING: {
    [Language.EN]: "Marketing",
    [Language.KR]: "마케팅 수신",
  },
  SETTINGS_APPROVAL_REQUESTS: {
    [Language.EN]: "Requests",
    [Language.KR]: "요청",
  },
  SETTINGS_CONNECTIONS: {
    [Language.EN]: "Management",
    [Language.KR]: "관리",
  },
  SETTINGS_PAGE_VISIBILITY: {
    [Language.EN]: "Page Visibility",
    [Language.KR]: "페이지 공개 설정",
  },
  SETTINGS_MENU_VISIBILITY: {
    [Language.EN]: "Menu Visibility",
    [Language.KR]: "메뉴 공개 설정",
  },
  SETTINGS_NAV_LAYOUT: {
    [Language.EN]: "Left Menu",
    [Language.KR]: "왼쪽 메뉴",
  },
  SETTINGS_SHARE: {
    [Language.EN]: "Share",
    [Language.KR]: "공유",
  },
  SETTINGS_SEARCH: {
    [Language.EN]: "Search",
    [Language.KR]: "검색",
  },
  SETTINGS_STORAGE: {
    [Language.EN]: "Storage",
    [Language.KR]: "저장 공간",
  },
  SEARCH_PREVIEW_OG_IMAGE_CUSTOM: {
    [Language.EN]: "OG Image: Custom",
    [Language.KR]: "OG 이미지: 커스텀",
  },
  SEARCH_PREVIEW_OG_IMAGE_DEFAULT: {
    [Language.EN]: "OG Image: Default",
    [Language.KR]: "OG 이미지: 기본값",
  },
  LINK_ID_EXPLAIN: {
    [Language.EN]: "A unique link to the archive.",
    [Language.KR]: "나의 Archive의 고유 링크입니다.",
  },
  USERNAME_EXPLAIN: {
    [Language.EN]: "This value is used by visitors to find and access your archive.",
    [Language.KR]: "방문자가 나의 Archive를 찾을 때 사용하는 값입니다.",
  },
  SHARE_EXPLAIN: {
    [Language.EN]: "These values are used for shared link previews.",
    [Language.KR]: "공유 링크 미리보기에 사용됩니다.",
  },
  SEARCH_EXPLAIN: {
    [Language.EN]: "These values are used for search results.",
    [Language.KR]: "Google 등 검색엔진 검색 결과에 사용됩니다.",
  },
  PAGE_EXPOSURE_PRIVATE_EXPLAIN: {
    [Language.EN]:
      "Visitors can't access your archive.",
    [Language.KR]:
      "Private 상태일 경우, 다른 사용자가 나의 Archive에 접근할 수 없습니다.",
  },
  PAGE_EXPOSURE_SEARCHABLE_EXPLAIN: {
    [Language.EN]:
      "Visitors can access your archive by searching. but it may not appear in the Archive Index.",
    [Language.KR]:
      "나의 Archive를 검색을 통해 접근할 수 있지만, Archive Index에 포함되지 않습니다.",
  },
  PAGE_EXPOSURE_PUBLIC_EXPLAIN: {
    [Language.EN]:
      "Visitors can access your archive in search and Archive Index.",
    [Language.KR]:
      "나의 Archive를 검색을 통해 접근할 수 있고, Archive Index에 포함됩니다.",
  },
  NAV_LAYOUT_EXPLAIN: {
    [Language.EN]:
      "Choose whether the left menu stays open or expands on hover.",
    [Language.KR]:
      "왼쪽 메뉴를 항상 보이게 할지, 마우스를 올리면 펼칠지 선택합니다.",
  },
  NAV_LAYOUT_FOLD: {
    [Language.EN]: "Fold",
    [Language.KR]: "접힘",
  },
  NAV_LAYOUT_FIXED: {
    [Language.EN]: "Fixed",
    [Language.KR]: "고정",
  },
  SUBSCRIPTION_MANAGED_BY: {
    [Language.EN]: "Subscription is securely managed by",
    [Language.KR]: "구독은 다음 서비스에서 안전하게 관리됩니다.",
  },
  PAYMENT_PROCESSOR: {
    [Language.EN]: "Polar.sh",
    [Language.KR]: "Polar.sh",
  },
  POLAR_OPEN_SUBSCRIPTION_PAGE: {
    [Language.EN]: "This will open Polar's subscription page.",
    [Language.KR]: "Polar 구독 관리 페이지가 열립니다.",
  },
  POLAR_OPEN_PAYMENT_PAGE: {
    [Language.EN]: "This will open Polar's payment page.",
    [Language.KR]: "Polar 결제 페이지가 열립니다.",
  },
  /** 줄바꿈은 `\n` — ProductCard에서 `<br />`로 렌더 */
  SUBSCRIPTION_PRODUCT_INTRO: {
    [Language.EN]: `Quiet. Light. Empty.\n\n\nDesigned for work that rests in quiet.`,
    [Language.KR]: `Quiet. Light. Empty.\n\n\n조용한 여백 위에 작품이 머무는 방식을 설계합니다.`,
  },
  SUBSCRIPTION_PRICE_INTERVAL_MONTH: {
    [Language.EN]: 'mo',
    [Language.KR]: '월',
  },
  SUBSCRIPTION_PRICE_INTERVAL_YEAR: {
    [Language.EN]: 'yr',
    [Language.KR]: '연',
  },
  SUBSCRIPTION_PRODUCT_FEATURE_STORAGE: {
    [Language.EN]: "10GB storage included",
    [Language.KR]: "저장 공간 10GB 제공",
  },
  SUBSCRIPTION_PRODUCT_FEATURE_UPLOADS: {
    [Language.EN]: "Effortless uploads & edits",
    [Language.KR]: "간편한 업로드 및 편집",
  },
  SUBSCRIPTION_PRODUCT_FEATURE_VISIBILITY: {
    [Language.EN]: "Public/private visibility controls",
    [Language.KR]: "공개·비공개 노출 제어",
  },
  SUBSCRIPTION_PRODUCT_FEATURE_CANCEL: {
    [Language.EN]: "Cancel anytime",
    [Language.KR]: "언제든 구독 해지 가능",
  },
  MARKETING_EMAIL_EXPLAIN: {
    [Language.EN]: "Allow Architrave to send you occasional updates and offers.",
    [Language.KR]: "소식 및 혜택 안내 메일 수신에 동의합니다.",
  },
  APPROVAL_REQUESTS_EXPLAIN: {
    [Language.EN]: "Review invitations and other approval requests.",
    [Language.KR]: "초대와 승인 요청을 확인합니다.",
  },
  CONNECTIONS_EXPLAIN: {
    [Language.EN]: "Review identity connections to Group Members managed by each group.",
    [Language.KR]: "각 그룹이 관리하는 GroupMember와의 계정 연결을 확인합니다.",
  },
  CONNECTION_NONE: {
    [Language.EN]: "No connected groups.",
    [Language.KR]: "연결된 그룹이 없습니다.",
  },
  CONNECTION_COUNT: {
    [Language.EN]: "Connected groups: {count}",
    [Language.KR]: "연결된 그룹 {count}개",
  },
  CONNECTION_LOADING: {
    [Language.EN]: "Loading connections...",
    [Language.KR]: "연결 정보를 불러오는 중입니다...",
  },
  CONNECTION_FAILED: {
    [Language.EN]: "Connections could not be loaded.",
    [Language.KR]: "연결 정보를 불러오지 못했습니다.",
  },
  APPROVAL_REQUEST_EMPTY: {
    [Language.EN]: "No requests.",
    [Language.KR]: "요청이 없습니다.",
  },
  APPROVAL_REQUEST_NONE: {
    [Language.EN]: "No request notifications.",
    [Language.KR]: "요청 알림이 없습니다.",
  },
  APPROVAL_REQUEST_COUNT: {
    [Language.EN]: "Request notifications: {count}",
    [Language.KR]: "요청 알림 {count}개",
  },
  APPROVAL_REQUEST_LOADING: {
    [Language.EN]: "Loading requests...",
    [Language.KR]: "요청을 불러오는 중입니다...",
  },
  APPROVAL_REQUEST_FAILED: {
    [Language.EN]: "Requests could not be loaded.",
    [Language.KR]: "요청을 불러오지 못했습니다.",
  },
  APPROVAL_REQUEST_ACTION_FAILED: {
    [Language.EN]: "The request could not be updated. Please try again.",
    [Language.KR]: "요청을 처리하지 못했습니다. 다시 시도해 주세요.",
  },
  APPROVAL_REQUEST_PROCESSING: {
    [Language.EN]: "Processing…",
    [Language.KR]: "처리 중…",
  },
  GROUP_MEMBER_CONNECTION_REQUEST_SUBJECT: {
    [Language.EN]: "member connection",
    [Language.KR]: "멤버 연결",
  },
  GROUP_MEMBER_CONNECTION_REQUEST_EXPLAIN: {
    [Language.EN]: "This connection may be ended at any time.",
    [Language.KR]: "연결은 필요에 따라 해제할 수 있습니다.",
  },
  GROUP_MEMBER_CONNECTION_INCOMING_PENDING: {
    [Language.EN]: "“{name}” proposes a member connection",
    [Language.KR]: "‘{name}’의 멤버 연결 요청",
  },
  GROUP_MEMBER_CONNECTION_INCOMING_ACCEPTED: {
    [Language.EN]: "Member connection with “{name}” accepted",
    [Language.KR]: "‘{name}’의 멤버 연결 수락",
  },
  GROUP_MEMBER_CONNECTION_INCOMING_REJECTED: {
    [Language.EN]: "Member connection with “{name}” rejected",
    [Language.KR]: "‘{name}’의 멤버 연결 거절",
  },
  GROUP_MEMBER_CONNECTION_INCOMING_CANCELED: {
    [Language.EN]: "Member connection from “{name}” canceled",
    [Language.KR]: "‘{name}’의 멤버 연결 요청 취소",
  },
  GROUP_MEMBER_CONNECTION_INCOMING_EXPIRED: {
    [Language.EN]: "Member connection from “{name}” expired",
    [Language.KR]: "‘{name}’의 멤버 연결 요청 만료",
  },
  WORK_USAGE_REQUEST_SUBJECT: {
    [Language.EN]: "use of “{title}”",
    [Language.KR]: "‘{title}’ 사용",
  },
  TEXT_COPY_REQUEST_SUBJECT: {
    [Language.EN]: "use of {count} Text(s)",
    [Language.KR]: "Text {count}개 사용",
  },
  TEXT_COPY_REQUEST_UNAVAILABLE: {
    [Language.EN]: "One or more Texts are no longer available, so this request cannot be accepted.",
    [Language.KR]: "더 이상 가져올 수 없는 Text가 있어 이 요청을 승인할 수 없습니다.",
  },
  TEXT_COPY_ITEM_UNAVAILABLE: {
    [Language.EN]: "Unavailable",
    [Language.KR]: "가져올 수 없음",
  },
  WORK_COPY_REQUEST_SUBJECT: {
    [Language.EN]: "use of {count} Work(s)",
    [Language.KR]: "Work {count}개 사용",
  },
  WORK_COPY_REQUEST_UNAVAILABLE: {
    [Language.EN]: "One or more Works are no longer available, so this request cannot be accepted.",
    [Language.KR]: "더 이상 가져올 수 없는 Work가 있어 이 요청을 승인할 수 없습니다.",
  },
  WORK_COPY_ITEM_UNAVAILABLE: {
    [Language.EN]: "Unavailable",
    [Language.KR]: "가져올 수 없음",
  },
  APPROVAL_REQUEST_INCOMING_PENDING: {
    [Language.EN]: "{subject} request from {name}",
    [Language.KR]: "{name}님의 {subject} 요청",
  },
  APPROVAL_REQUEST_OUTGOING_PENDING: {
    [Language.EN]: "{subject} request to {name}",
    [Language.KR]: "{name}님에게 {subject} 요청",
  },
  APPROVAL_REQUEST_INCOMING_ACCEPTED: {
    [Language.EN]: "{subject} request from {name} accepted",
    [Language.KR]: "{name}님의 {subject} 요청 수락",
  },
  APPROVAL_REQUEST_OUTGOING_ACCEPTED: {
    [Language.EN]: "{subject} request accepted by {name}",
    [Language.KR]: "{name}님의 {subject} 수락",
  },
  APPROVAL_REQUEST_INCOMING_REJECTED: {
    [Language.EN]: "{subject} request from {name} rejected",
    [Language.KR]: "{name}님의 {subject} 요청 거절",
  },
  APPROVAL_REQUEST_OUTGOING_REJECTED: {
    [Language.EN]: "{subject} request to {name} rejected",
    [Language.KR]: "{name}님의 {subject} 거절",
  },
  APPROVAL_REQUEST_INCOMING_CANCELED: {
    [Language.EN]: "{subject} request from {name} canceled",
    [Language.KR]: "{name}님의 {subject} 요청 취소",
  },
  APPROVAL_REQUEST_OUTGOING_CANCELED: {
    [Language.EN]: "{subject} request canceled",
    [Language.KR]: "{subject} 요청 취소",
  },
  APPROVAL_REQUEST_INCOMING_EXPIRED: {
    [Language.EN]: "{subject} request from {name} expired",
    [Language.KR]: "{name}님의 {subject} 요청 만료",
  },
  APPROVAL_REQUEST_OUTGOING_EXPIRED: {
    [Language.EN]: "{subject} request to {name} expired",
    [Language.KR]: "{name}님에게 {subject} 요청 만료",
  },
};

/**
 * 페이지 공통 메시지를 가져오는 함수 (빈 목록 등)
 * 언어는 languageStore에서 자동으로 가져옵니다.
 * alertMessages의 getAlertMessage와 동일하게 string만 반환합니다.
 */
export function getPageMessage(
  key: PageMessageKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = pageMessages[key];

  if (!message) {
    console.warn(`Page message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      const safeValue = paramValue !== undefined && paramValue !== null
        ? String(paramValue)
        : '';
      const pattern = new RegExp(`\\{${paramKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\}`, 'g');
      translatedMessage = translatedMessage.replace(pattern, safeValue);
    });
  }

  return translatedMessage;
}

/** CareerType별 섹션 제목. 호출부가 언어를 넘긴다. */
const CAREER_SECTION_TITLES: Record<CareerType, Record<Language, string>> = {
  [CareerType.EDU]: {
    [Language.EN]: 'Education',
    [Language.KR]: '학력',
  },
  [CareerType.WRT]: {
    [Language.EN]: 'Writing',
    [Language.KR]: '저술',
  },
  [CareerType.CNS]: {
    [Language.EN]: 'Consulting',
    [Language.KR]: '컨설팅',
  },
  [CareerType.S_EXH]: {
    [Language.EN]: 'Solo Exhibition',
    [Language.KR]: '개인전',
  },
  [CareerType.G_EXH]: {
    [Language.EN]: 'Selected Group Exhibitions',
    [Language.KR]: '주요 단체전',
  },
  [CareerType.BIEN_TRIEN]: {
    [Language.EN]: 'Biennial/Triennial',
    [Language.KR]: '비엔날레',
  },
  [CareerType.PRJ]: {
    [Language.EN]: 'Project',
    [Language.KR]: '프로젝트',
  },
  [CareerType.FAR]: {
    [Language.EN]: 'Fair',
    [Language.KR]: '아트페어',
  },
  [CareerType.AWD_GRT]: {
    [Language.EN]: 'Awards & Grants',
    [Language.KR]: '수상 및 선정',
  },
  [CareerType.PRS]: {
    [Language.EN]: 'Press',
    [Language.KR]: '기사/언론 보도',
  },
  [CareerType.RSD]: {
    [Language.EN]: 'Residency',
    [Language.KR]: '레지던시',
  },
  [CareerType.RPS]: {
    [Language.EN]: 'Representation',
    [Language.KR]: '소속',
  },
  [CareerType.EXP]: {
    [Language.EN]: 'Professional Experience',
    [Language.KR]: '실무 경력',
  },
  [CareerType.TCH]: {
    [Language.EN]: 'Teach',
    [Language.KR]: '강의',
  },
  [CareerType.RSH]: {
    [Language.EN]: 'Research',
    [Language.KR]: '연구/조사',
  },
  [CareerType.PBL]: {
    [Language.EN]: 'Publication',
    [Language.KR]: '출판',
  },
  [CareerType.COL]: {
    [Language.EN]: 'Collections',
    [Language.KR]: '작품 소장',
  },
};

export function getCareerTypeTitle(
  careerType: CareerType,
  language: Language = Language.EN,
): string {
  const row = CAREER_SECTION_TITLES[careerType];
  if (!row) return '';
  return row[language] || row[Language.EN];
}

/** CareerType별 경력 본문 요약 placeholder (전역 앱 언어 기준) */
const CAREER_SUMMARY_PLACEHOLDERS: Record<CareerType, Record<Language, string>> = {
  [CareerType.EDU]: {
    [Language.EN]: 'e.g. School, Major, Degree',
    [Language.KR]: '학교명, 전공, 학위',
  },
  [CareerType.EXP]: {
    [Language.EN]: 'e.g. Position, Institution',
    [Language.KR]: '직함, 기관명',
  },
  [CareerType.WRT]: {
    [Language.EN]: 'e.g. Article or Essay Title, Publication',
    [Language.KR]: '글 제목, 매체명',
  },
  [CareerType.CNS]: {
    [Language.EN]: 'e.g. Advisory Role, Organization or Project',
    [Language.KR]: '자문 역할, 기관 또는 프로젝트명',
  },
  [CareerType.S_EXH]: {
    [Language.EN]: 'e.g. Exhibition Title, Gallery, City',
    [Language.KR]: '전시명, 장소, 도시',
  },
  [CareerType.G_EXH]: {
    [Language.EN]: 'e.g. Exhibition Title, Venue, City',
    [Language.KR]: '전시명, 장소, 도시',
  },
  [CareerType.BIEN_TRIEN]: {
    [Language.EN]: 'e.g. Event Title, City',
    [Language.KR]: '전시명, 도시',
  },
  [CareerType.PRJ]: {
    [Language.EN]: 'e.g. Project or program name, partner / host, city or online',
    [Language.KR]: '프로젝트·프로그램명, 협업 기관·주최, 도시 또는 온라인',
  },
  [CareerType.FAR]: {
    [Language.EN]: 'e.g. Fair Name, Venue/City',
    [Language.KR]: '전시명, 장소/도시',
  },
  [CareerType.AWD_GRT]: {
    [Language.EN]: 'e.g. Award or grant, organization',
    [Language.KR]: '상·선정명, 기관',
  },
  [CareerType.PRS]: {
    [Language.EN]: 'e.g. Publication Name, Article Title',
    [Language.KR]: '매체명, 기사/리뷰 제목',
  },
  [CareerType.RSD]: {
    [Language.EN]: 'e.g. Program Name, Institution/City',
    [Language.KR]: '프로그램명, 기관/도시',
  },
  [CareerType.RPS]: {
    [Language.EN]: 'e.g. Gallery Name, City',
    [Language.KR]: '갤러리명, 도시',
  },
  [CareerType.TCH]: {
    [Language.EN]: 'e.g. Title, School/Institution',
    [Language.KR]: '직함, 학교/기관명',
  },
  [CareerType.RSH]: {
    [Language.EN]: 'e.g. Research topic, Institution',
    [Language.KR]: '연구 주제, 기관명',
  },
  [CareerType.PBL]: {
    [Language.EN]: 'e.g. Publication Title, Publisher',
    [Language.KR]: '출판물명, 발행처',
  },
  [CareerType.COL]: {
    [Language.EN]: 'e.g. Collection/Institution Name',
    [Language.KR]: '소장처, 기관명',
  },
};

/** About에서 ENG 필드일 때 placeholder 끝에 붙는 언어 표기 (앱 UI 언어 기준) */
const ABOUT_ENG_FIELD_SUFFIX: Record<Language, string> = {
  [Language.EN]: ' (ENG)',
  [Language.KR]: ' (영문)',
};

function withAboutEngFieldSuffix(base: string): string {
  const language = useLanguageStore.getState().language;
  const aboutContentMode = useAboutViewStore.getState().aboutContentMode;
  if (aboutContentMode !== AboutContentMode.ENG) return base;
  const suffix = ABOUT_ENG_FIELD_SUFFIX[language] || ABOUT_ENG_FIELD_SUFFIX[Language.EN];
  return `${base}${suffix}`;
}

/** About 경력 placeholder. ENG 접미사 여부는 `aboutViewStore`에서 읽음 */
export function getCareerContentSummaryPlaceholder(careerType: CareerType): string {
  const language = useLanguageStore.getState().language;
  const row = CAREER_SUMMARY_PLACEHOLDERS[careerType];
  if (!row) {
    console.warn(`Career summary placeholder not defined for careerType: ${careerType}`);
    return getPageMessage('DESCRIPTION_PLACEHOLDER_DEFAULT');
  }
  const base = row[language] || row[Language.EN];
  return withAboutEngFieldSuffix(base);
}

/** Member 소개문 placeholder (기본 문구 + About ENG일 때 접미사) */
export function getMemberDescriptionPlaceholder(): string {
  return withAboutEngFieldSuffix(getPageMessage('DESCRIPTION_PLACEHOLDER_DEFAULT'));
}

/** Member 이름 입력 placeholder (기본 문구 + About ENG일 때 접미사) */
export function getMemberNamePlaceholder(): string {
  return withAboutEngFieldSuffix(getPageMessage('MEMBER_NAME_PLACEHOLDER'));
}

/**
 * Works 페이지 메시지를 가져오는 함수
 */
export function getWorksMessage(
  key: worksMessagesKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = worksMessages[key];

  if (!message) {
    console.warn(`Works message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}

/**
 * Settings 안내 메시지를 가져오는 함수
 * 언어는 languageStore에서 자동으로 가져옵니다.
 */
export function getSettingsMessage(
  key: SettingsMessageKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = settingsMessages[key];

  if (!message) {
    console.warn(`Settings message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}

/** `PageSetting` — `navLayout` 옵션 라벨 */
export function getNavLayoutLabel(layout: NavLayout): string {
  switch (layout) {
    case NavLayout.FOLD:
      return getSettingsMessage('NAV_LAYOUT_FOLD');
    case NavLayout.FIXED:
      return getSettingsMessage('NAV_LAYOUT_FIXED');
    default: {
      const _exhaustive: never = layout;
      return _exhaustive;
    }
  }
}

/** `PageSetting` — `exposureLevel`에 맞는 노출 안내 문구 */
export function getPageExposureExplainMessage(level: ExposureLevel): string {
  switch (level) {
    case ExposureLevel.PRIVATE:
      return getSettingsMessage('PAGE_EXPOSURE_PRIVATE_EXPLAIN');
    case ExposureLevel.SEARCHABLE:
      return getSettingsMessage('PAGE_EXPOSURE_SEARCHABLE_EXPLAIN');
    case ExposureLevel.PUBLIC:
      return getSettingsMessage('PAGE_EXPOSURE_PUBLIC_EXPLAIN');
    default: {
      const _exhaustive: never = level;
      return _exhaustive;
    }
  }
}

/**
 * Landing 페이지 메시지를 가져오는 함수
 */
export function getLandingMessage(
  key: landingMessagesKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = landingMessages[key];

  if (!message) {
    console.warn(`Landing message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}

/**
 * Contact 페이지 안내 메시지를 가져오는 함수
 */
export function getContactsMessage(
  key: contactsMessagesKey,
  params?: Record<string, string | number>
): string {
  const language = useLanguageStore.getState().language;
  const message = contactsMessages[key];

  if (!message) {
    console.warn(`Contacts message key "${key}" not found.`);
    return key;
  }

  let translatedMessage = message[language] || message[Language.EN];

  if (params) {
    Object.entries(params).forEach(([paramKey, paramValue]) => {
      translatedMessage = translatedMessage.replace(
        new RegExp(`\\{${paramKey}\\}`, 'g'),
        String(paramValue)
      );
    });
  }

  return translatedMessage;
}
